import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Clock,
  Bell,
  Calendar,
  Plus,
  Trash2,
  Play,
  Pause,
  RefreshCw,
  Check,
  X,
  
  AlertCircle,
} from 'lucide-react'
import { cn } from '@/utils'

interface ScheduledTask {
  id: string
  name: string
  description?: string
  task_type: 'reminder' | 'payment' | 'notification' | 'custom'
  schedule_type: 'once' | 'interval' | 'cron'
  status: 'active' | 'paused' | 'completed' | 'failed'
  next_run_at?: string
  last_run_at?: string
  run_count: number
  max_runs?: number
  payload?: Record<string, unknown>
  created_at: string
}

const getAuthToken = () => localStorage.getItem('niki_access_token')

const taskTypeIcons = {
  reminder: Bell,
  payment: Calendar,
  notification: Bell,
  custom: Clock,
}

const taskTypeColors = {
  reminder: 'text-blue-400 bg-blue-500/20',
  payment: 'text-green-400 bg-green-500/20',
  notification: 'text-yellow-400 bg-yellow-500/20',
  custom: 'text-purple-400 bg-purple-500/20',
}

const statusColors = {
  active: 'text-green-400',
  paused: 'text-yellow-400',
  completed: 'text-white/50',
  failed: 'text-red-400',
}

export function ScheduledTasksPage() {
  const [tasks, setTasks] = useState<ScheduledTask[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddModal, setShowAddModal] = useState(false)
  const [filter, setFilter] = useState<'all' | 'active' | 'completed'>('all')

  useEffect(() => {
    fetchTasks()
  }, [])

  const fetchTasks = async () => {
    try {
      const res = await fetch('/api/v1/scheduler', {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      })
      if (res.ok) {
        const data = await res.json()
        setTasks(data.tasks || [])
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error)
    } finally {
      setLoading(false)
    }
  }

  const toggleTask = async (taskId: string, action: 'pause' | 'resume') => {
    try {
      const res = await fetch(`/api/v1/scheduler/${taskId}/${action}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      })
      if (res.ok) {
        fetchTasks()
      }
    } catch (error) {
      console.error('Failed to toggle task:', error)
    }
  }

  const deleteTask = async (taskId: string) => {
    if (!confirm('Delete this scheduled task?')) return
    
    try {
      const res = await fetch(`/api/v1/scheduler/${taskId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      })
      if (res.ok) {
        setTasks(tasks.filter(t => t.id !== taskId))
      }
    } catch (error) {
      console.error('Failed to delete task:', error)
    }
  }

  const runNow = async (taskId: string) => {
    try {
      const res = await fetch(`/api/v1/scheduler/${taskId}/run`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      })
      if (res.ok) {
        fetchTasks()
      }
    } catch (error) {
      console.error('Failed to run task:', error)
    }
  }

  const filteredTasks = tasks.filter(t => {
    if (filter === 'all') return true
    if (filter === 'active') return t.status === 'active' || t.status === 'paused'
    if (filter === 'completed') return t.status === 'completed'
    return true
  })

  const formatNextRun = (dateStr?: string) => {
    if (!dateStr) return 'Not scheduled'
    const date = new Date(dateStr)
    const now = new Date()
    const diff = date.getTime() - now.getTime()
    
    if (diff < 0) return 'Overdue'
    if (diff < 60000) return 'In less than a minute'
    if (diff < 3600000) return `In ${Math.floor(diff / 60000)} minutes`
    if (diff < 86400000) return `In ${Math.floor(diff / 3600000)} hours`
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Scheduled Tasks</h1>
          <p className="text-white/50 text-sm">Reminders, payments, and automations</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn-primary flex items-center gap-2 px-4 py-2"
        >
          <Plus className="w-4 h-4" />
          Schedule
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6">
        {(['all', 'active', 'completed'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={cn(
              'px-4 py-2 rounded-lg text-sm capitalize transition-colors',
              filter === f
                ? 'bg-primary/20 text-primary-light'
                : 'text-white/60 hover:bg-white/5'
            )}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Tasks List */}
      {loading ? (
        <div className="text-center py-12 text-white/50">Loading...</div>
      ) : filteredTasks.length === 0 ? (
        <div className="text-center py-12">
          <Clock className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <p className="text-white/50">No scheduled tasks</p>
          <p className="text-white/30 text-sm">Create reminders and recurring tasks</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredTasks.map((task) => {
            const Icon = taskTypeIcons[task.task_type] || Clock
            return (
              <motion.div
                key={task.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card p-4 group"
              >
                <div className="flex items-start gap-4">
                  <div className={cn(
                    'w-10 h-10 rounded-lg flex items-center justify-center',
                    taskTypeColors[task.task_type]
                  )}>
                    <Icon className="w-5 h-5" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-white truncate">{task.name}</h3>
                      <span className={cn('text-xs', statusColors[task.status])}>
                        • {task.status}
                      </span>
                    </div>
                    {task.description && (
                      <p className="text-white/50 text-sm truncate">{task.description}</p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-white/40">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatNextRun(task.next_run_at)}
                      </span>
                      {task.run_count > 0 && (
                        <span className="flex items-center gap-1">
                          <RefreshCw className="w-3 h-3" />
                          {task.run_count} runs
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {task.status === 'active' && (
                      <button
                        onClick={() => toggleTask(task.id, 'pause')}
                        className="p-2 rounded-lg hover:bg-white/10"
                        title="Pause"
                      >
                        <Pause className="w-4 h-4 text-white/60" />
                      </button>
                    )}
                    {task.status === 'paused' && (
                      <button
                        onClick={() => toggleTask(task.id, 'resume')}
                        className="p-2 rounded-lg hover:bg-white/10"
                        title="Resume"
                      >
                        <Play className="w-4 h-4 text-white/60" />
                      </button>
                    )}
                    <button
                      onClick={() => runNow(task.id)}
                      className="p-2 rounded-lg hover:bg-white/10"
                      title="Run now"
                    >
                      <Play className="w-4 h-4 text-primary" />
                    </button>
                    <button
                      onClick={() => deleteTask(task.id)}
                      className="p-2 rounded-lg hover:bg-red-500/20"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>
      )}

      {/* Add Modal */}
      <AnimatePresence>
        {showAddModal && (
          <AddScheduledTaskModal
            onClose={() => setShowAddModal(false)}
            onSave={() => {
              setShowAddModal(false)
              fetchTasks()
            }}
          />
        )}
      </AnimatePresence>
    </div>
  )
}

// Add Task Modal
function AddScheduledTaskModal({
  onClose,
  onSave,
}: {
  onClose: () => void
  onSave: () => void
}) {
  const [form, setForm] = useState({
    name: '',
    description: '',
    task_type: 'reminder' as 'reminder' | 'payment' | 'notification' | 'custom',
    schedule_type: 'once' as const,
    scheduled_at: '',
    payload: { message: '' },
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Name is required')
      return
    }

    setSaving(true)
    setError('')

    try {
      const res = await fetch('/api/v1/scheduler', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({
          ...form,
          scheduled_at: form.scheduled_at ? new Date(form.scheduled_at).toISOString() : undefined,
        }),
      })

      if (res.ok) {
        onSave()
      } else {
        const data = await res.json()
        setError(data.detail || 'Failed to create task')
      }
    } catch (err) {
      setError('Failed to create task')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="fixed inset-4 z-50 flex items-center justify-center pointer-events-none"
      >
        <div className="glass-card w-full max-w-md max-h-[90vh] overflow-y-auto pointer-events-auto">
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <h2 className="text-xl font-semibold text-white">Schedule Task</h2>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10">
              <X className="w-5 h-5 text-white/60" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            {error && (
              <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <div>
              <label className="text-sm text-white/70 mb-1 block">Task Name *</label>
              <input
                type="text"
                placeholder="e.g., Call mom"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white placeholder-white/40 focus:outline-none focus:border-primary/50"
              />
            </div>

            <div>
              <label className="text-sm text-white/70 mb-1 block">Type</label>
              <div className="flex gap-2">
                {(['reminder', 'payment', 'notification'] as const).map((type) => {
                  const Icon = taskTypeIcons[type]
                  return (
                    <button
                      key={type}
                      onClick={() => setForm({ ...form, task_type: type })}
                      className={cn(
                        'flex-1 p-3 rounded-lg border flex flex-col items-center gap-1 transition-colors capitalize',
                        form.task_type === type
                          ? 'border-primary bg-primary/10'
                          : 'border-white/10 hover:border-white/20'
                      )}
                    >
                      <Icon className={cn(
                        'w-5 h-5',
                        form.task_type === type ? 'text-primary' : 'text-white/60'
                      )} />
                      <span className={cn(
                        'text-xs',
                        form.task_type === type ? 'text-white' : 'text-white/60'
                      )}>
                        {type}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>

            <div>
              <label className="text-sm text-white/70 mb-1 block">When</label>
              <input
                type="datetime-local"
                value={form.scheduled_at}
                onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-primary/50"
              />
            </div>

            <div>
              <label className="text-sm text-white/70 mb-1 block">Message/Notes</label>
              <textarea
                placeholder="What should I remind you about?"
                value={form.payload.message}
                onChange={(e) => setForm({ ...form, payload: { ...form.payload, message: e.target.value } })}
                rows={3}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white placeholder-white/40 focus:outline-none focus:border-primary/50 resize-none"
              />
            </div>
          </div>

          <div className="p-6 border-t border-white/10 flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 rounded-lg border border-white/20 text-white/70 hover:bg-white/5"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 btn-primary flex items-center justify-center gap-2"
            >
              {saving ? 'Scheduling...' : (
                <>
                  <Check className="w-4 h-4" />
                  Schedule
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </>
  )
}
